/**
 * Gated HITL bridge for `set_structure_color` — changes a structure's
 * display colour exactly as the structure list's colour picker does. The
 * colour is saved with the structure set (DICOM ROI Display Color) and the
 * picker refuses it for approved structures, so — unlike visibility — it is
 * an edit behind an Apply/Reject card.
 *
 * Flow: parse the #RRGGBB colour → resolve the structure (any structure,
 * empty and point ROIs included, like the picker) → refuse approved ones →
 * a {@link ColorProposal} showing old and new colour. Apply re-checks the
 * structure set and approval, applies the colour, then reads the colour
 * back: the existing command reports problems only as a viewer message, so
 * the read-back is what confirms it. Pure/React-free; ports injected.
 */
import { resolveRoi } from '../core/roiResolver';
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    reasonOf,
    resolveFailureToPlan,
    toListed,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
    type StructureFacts,
} from '../core';
import type { SetStructureColorOutcome } from '../../contract';

/** Colour as the viewer stores it: 0–255 per channel. */
export interface Rgb {
    readonly R: number;
    readonly G: number;
    readonly B: number;
}

export function hexToRgb(hex: string): Rgb | undefined {
    const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
    return m
        ? {
              R: parseInt(m[1], 16),
              G: parseInt(m[2], 16),
              B: parseInt(m[3], 16),
          }
        : undefined;
}

export function rgbToHex(c: Rgb): string {
    const h = (n: number) =>
        Math.max(0, Math.min(255, Math.round(n)))
            .toString(16)
            .padStart(2, '0');
    return `#${h(c.R)}${h(c.G)}${h(c.B)}`.toUpperCase();
}

export interface ColorDeps extends PendingFlag {
    readonly structures: ReadonlyArray<StructureFacts>;
    /** False for an approved structure (the picker is disabled then). */
    canModify(roiNumber: number): boolean;
    /** The structure's colour now, read live; undefined if gone. */
    currentColor(roiNumber: number): Rgb | undefined;
    /** Identity of the active structure set, read live. */
    currentContextId(): string | undefined;
    /** The colour picker's command; resolves once the colour is set. */
    setColor(roiNumber: number, color: Rgb): void | Promise<void>;
}

export interface ColorProposal {
    readonly toolCallId: string;
    readonly target: { readonly roiNumber: number; readonly name: string };
    /** #RRGGBB */
    readonly fromColor: string;
    /** #RRGGBB */
    readonly toColor: string;
    apply(): Promise<SetStructureColorOutcome>;
    reject(): SetStructureColorOutcome;
    /** No geometry to preview; the card shows both colours. */
    showPreview(): void;
    hidePreview(): void;
}

export type ColorPlan = MutatingPlan<ColorProposal>;

const notModifiable = (name: string) =>
    `'${name}' is approved; its colour cannot be changed`;

export async function planSetStructureColor(
    call: MutatingToolCall,
    deps: ColorDeps
): Promise<ColorPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input);
    const color = typeof o?.color === 'string' ? hexToRgb(o.color) : undefined;
    if (typeof o?.roi !== 'string' || !o.roi || !color) {
        return failedPlan(
            call.toolCallId,
            'invalid set_structure_color input (needs a structure name and ' +
                'a #RRGGBB colour)'
        );
    }
    const resolution = resolveRoi(o.roi, deps.structures.map(toListed));
    if (resolution.kind !== 'resolved') {
        return resolveFailureToPlan(
            resolution.kind === 'ambiguous'
                ? {
                      kind: 'ambiguous',
                      reason: resolution.reason,
                      candidates: resolution.candidates,
                  }
                : { kind: 'not-found', suggestions: resolution.suggestions },
            call.toolCallId
        );
    }
    const { roiNumber, name } = resolution.structure;
    if (!deps.canModify(roiNumber)) {
        return failedPlan(call.toolCallId, notModifiable(name));
    }
    const before = deps.currentColor(roiNumber);
    const toColor = rgbToHex(color);
    if (before && rgbToHex(before) === toColor) {
        return failedPlan(
            call.toolCallId,
            `'${name}' is already ${toColor}; nothing to change`
        );
    }
    const plannedContextId = deps.currentContextId();

    const proposal: ColorProposal = {
        toolCallId: call.toolCallId,
        target: { roiNumber, name },
        fromColor: before ? rgbToHex(before) : '',
        toColor,
        apply: async () => {
            if (deps.currentContextId() !== plannedContextId) {
                return {
                    status: 'failed',
                    reason:
                        'the active structure set changed before this was ' +
                        'applied; re-issue the request',
                };
            }
            if (!deps.canModify(roiNumber)) {
                return { status: 'failed', reason: notModifiable(name) };
            }
            try {
                await deps.setColor(roiNumber, color);
            } catch (err) {
                return { status: 'failed', reason: reasonOf(err) };
            }
            const after = deps.currentColor(roiNumber);
            if (!after || rgbToHex(after) !== toColor) {
                return {
                    status: 'failed',
                    reason: 'the viewer did not apply the colour; see its message',
                };
            }
            return { status: 'applied', roiNumber, roi: name, color: toColor };
        },
        reject: () => ({ status: 'rejected' }),
        showPreview: () => undefined,
        hidePreview: () => undefined,
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
